import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { BookOpen, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '../../auth';
import { useNavigation } from '../../context/NavigationContext';

interface Section {
  id: string;
  label: string;
  requiresManager?: boolean;
  requiresHR?: boolean;
}

const SECTIONS: Section[] = [
  { id: 'erste-schritte', label: 'Erste Schritte' },
  { id: 'kalender', label: 'Kalender' },
  { id: 'urlaub-feiertage', label: 'Urlaub & Feiertage' },
  { id: 'urlaubsbuchung', label: 'Urlaubsbuchung' },
  { id: 'vorlaeufig', label: 'Vorläufige Urlaubsstunden' },
  { id: 'einstellungen', label: 'Einstellungen' },
  { id: 'auswertung', label: 'Auswertung & Dashboard' },
  { id: 'manager', label: 'Manager-Bereich', requiresManager: true },
  { id: 'hr', label: 'HR-Bereich', requiresHR: true },
];

function ScreenshotPlaceholder({ description }: { description: string }) {
  return (
    <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 my-4 flex items-center justify-center min-h-[120px] bg-gray-50 dark:bg-gray-800/50">
      <p className="text-sm text-gray-400 dark:text-gray-500 text-center italic">{description}</p>
    </div>
  );
}

export function HelpPage() {
  const { isHR, isManager } = useAuth();
  const { helpSection } = useNavigation();
  const [activeSection, setActiveSection] = useState<string>('erste-schritte');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const hasScrolled = useRef(false);

  // useMemo: visibleSections sind Filter-Result und muessen stabil bleiben,
  // sonst triggert der IntersectionObserver-Effect jeden Render neu und
  // baut den Observer unnoetig auf/ab.
  const visibleSections = useMemo(
    () =>
      SECTIONS.filter((s) => {
        if (s.requiresHR) return isHR;
        if (s.requiresManager) return isManager;
        return true;
      }),
    [isHR, isManager],
  );

  // Scroll to section from NavigationContext. Flag wird bei jedem
  // helpSection-Wechsel zurueckgesetzt, damit sich die Komponente bei
  // wiederholtem Navigieren (z. B. ueber CMD+K) erneut positioniert.
  useEffect(() => {
    hasScrolled.current = false;
    if (!helpSection) return;
    const timer = setTimeout(() => {
      const el = document.getElementById(`help-${helpSection}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        hasScrolled.current = true;
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [helpSection]);

  // IntersectionObserver for active section tracking
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const id = entry.target.id.replace('help-', '');
            setActiveSection(id);
          }
        }
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: 0 }
    );

    const sectionEls = visibleSections
      .map((s) => document.getElementById(`help-${s.id}`))
      .filter(Boolean) as HTMLElement[];

    sectionEls.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [visibleSections]);

  const scrollToSection = useCallback((id: string) => {
    const el = document.getElementById(`help-${id}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setMobileNavOpen(false);
  }, []);

  return (
    <div className="max-w-6xl mx-auto">
      <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2 mb-6">
        <BookOpen className="w-6 h-6" />
        Hilfe
      </h2>

      <div className="lg:flex lg:gap-8">
        {/* Mobile: Dropdown navigation */}
        <div className="lg:hidden mb-4">
          <button
            onClick={() => setMobileNavOpen(!mobileNavOpen)}
            aria-expanded={mobileNavOpen}
            aria-controls="help-mobile-nav"
            className="w-full flex items-center justify-between px-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200"
          >
            <span>{visibleSections.find((s) => s.id === activeSection)?.label ?? 'Navigation'}</span>
            <ChevronDown className={cn('w-4 h-4 transition-transform', mobileNavOpen && 'rotate-180')} />
          </button>
          {/* Immer rendern (hidden-Toggle via CSS) damit aria-controls
              auf ein existierendes Element zeigt — sonst dangling Ref. */}
          <nav
            id="help-mobile-nav"
            hidden={!mobileNavOpen}
            className="mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-white/10 rounded-lg overflow-hidden"
          >
            {visibleSections.map((s) => (
              <button
                key={s.id}
                onClick={() => scrollToSection(s.id)}
                className={cn(
                  'block w-full text-left px-4 py-2.5 text-sm transition-colors',
                  activeSection === s.id
                    ? 'bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 font-medium'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                )}
              >
                {s.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Desktop: Sticky sidebar */}
        <aside className="hidden lg:block lg:w-56 flex-shrink-0">
          <nav className="sticky top-24 space-y-1">
            {visibleSections.map((s) => (
              <button
                key={s.id}
                onClick={() => scrollToSection(s.id)}
                className={cn(
                  'block w-full text-left px-3 py-2 rounded-lg text-sm transition-colors',
                  activeSection === s.id
                    ? 'bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 font-medium'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-800 dark:hover:text-gray-200'
                )}
              >
                {s.label}
              </button>
            ))}
          </nav>
        </aside>

        {/* Content */}
        <div ref={contentRef} className="flex-1 min-w-0 space-y-12">
          {/* Erste Schritte */}
          <section id="help-erste-schritte" className="scroll-mt-24">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 border-b border-gray-200 dark:border-gray-700 pb-2">
              Erste Schritte
            </h3>
            <div className="prose dark:prose-invert max-w-none space-y-4 text-sm text-gray-700 dark:text-gray-300">
              <h4 className="text-base font-medium text-gray-800 dark:text-gray-100">Anmeldung</h4>
              <p>
                Die Anmeldung erfolgt über Ihr Firmen-Microsoft-Konto (Azure AD). Klicken Sie auf der
                Login-Seite auf <strong>&quot;Mit Microsoft anmelden&quot;</strong> und geben Sie Ihre
                Firmenzugangsdaten ein.
              </p>
              <ScreenshotPlaceholder description="Screenshot: Login-Seite mit Microsoft-Anmeldebutton" />

              <h4 className="text-base font-medium text-gray-800 dark:text-gray-100">Jahresauswahl</h4>
              <p>
                Oben in der Kopfzeile finden Sie ein Dropdown zur Jahresauswahl. Jedes Jahr wird
                separat verwaltet — Ihre Einträge, Urlaubstage und Einstellungen sind pro Jahr gespeichert.
              </p>

              <h4 className="text-base font-medium text-gray-800 dark:text-gray-100">Navigation</h4>
              <p>
                Ihre <strong>Startseite</strong> zeigt alle wichtigen Informationen auf einen Blick:
                Urlaubssaldo mit Berechnungserkl&auml;rung, Kalender und Statistiken. Klappen
                Sie <strong>Monats&uuml;bersicht</strong> oder <strong>Details anzeigen</strong> auf
                f&uuml;r weitere Informationen. &Uuml;ber die Navigation erreichen Sie <strong>Hilfe</strong> und{' '}
                <strong>FAQ</strong>.
              </p>

              <h4 className="text-base font-medium text-gray-800 dark:text-gray-100">Rollen</h4>
              <p>Es gibt drei Rollen mit unterschiedlichen Berechtigungen:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Mitarbeiter</strong> — Eigene Arbeitszeiten und Urlaubstage verwalten</li>
                <li><strong>Manager</strong> — Zusätzlich: Team-Kalender einsehen, Anmerkungen hinterlassen</li>
                <li><strong>HR</strong> — Zusätzlich: Alle Mitarbeiter verwalten, Betriebsdaten ändern</li>
              </ul>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Ihre Rolle wird automatisch über Ihre Microsoft-Gruppenzugehörigkeit bestimmt.
              </p>
            </div>
          </section>

          {/* Kalender */}
          <section id="help-kalender" className="scroll-mt-24">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 border-b border-gray-200 dark:border-gray-700 pb-2">
              Kalender
            </h3>
            <div className="prose dark:prose-invert max-w-none space-y-4 text-sm text-gray-700 dark:text-gray-300">
              <h4 className="text-base font-medium text-gray-800 dark:text-gray-100">Monatsansicht</h4>
              <p>
                Der Kalender zeigt Ihnen alle Monate des gewählten Jahres. Jeder Tag wird als Zelle
                dargestellt — mit Farben, die den Status kennzeichnen.
              </p>

              <h4 className="text-base font-medium text-gray-800 dark:text-gray-100">Farbcodierung</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 my-3">
                <div className="flex items-center gap-2">
                  <span className="w-4 h-4 rounded bg-gray-200 dark:bg-gray-600 border" />
                  <span>Wochenende</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-4 h-4 rounded bg-yellow-200 dark:bg-yellow-800 border" />
                  <span>Feiertag</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-4 h-4 rounded bg-blue-200 dark:bg-blue-800 border" />
                  <span>Urlaub</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-4 h-4 rounded bg-orange-200 dark:bg-orange-800 border" />
                  <span>Krankheit</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-4 h-4 rounded bg-green-100 dark:bg-green-900 border" />
                  <span>Stunden eingetragen</span>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className="w-4 h-4 rounded border border-dashed border-purple-400 dark:border-purple-500"
                    style={{
                      background:
                        'repeating-linear-gradient(45deg, transparent, transparent 3px, rgba(168, 85, 247, 0.18) 3px, rgba(168, 85, 247, 0.18) 6px)',
                    }}
                  />
                  <span>Geplant (vorgemerkt)</span>
                </div>
              </div>
              <ScreenshotPlaceholder description="Screenshot: Kalenderansicht mit verschiedenen Tagestypen" />

              <h4 className="text-base font-medium text-gray-800 dark:text-gray-100">Tag bearbeiten</h4>
              <p>
                Klicken Sie auf einen Tag, um das Bearbeitungsfenster zu öffnen. Dort können Sie:
              </p>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Arbeitsstunden</strong> eingeben (0–24, Dezimalzahlen wie 4,5 erlaubt)</li>
                <li>Den Tag als <strong>Feiertag</strong>, <strong>Urlaubstag</strong> oder <strong>Krankheitstag</strong> markieren</li>
                <li>Eine optionale <strong>Bemerkung</strong> hinterlassen (max. 200 Zeichen)</li>
              </ul>

              <h4 className="text-base font-medium text-gray-800 dark:text-gray-100">Automatische Stundenberechnung</h4>
              <p>
                Bei <strong>Krankheit</strong> und <strong>Urlaub</strong> werden die Stunden automatisch
                aus dem 13-Wochen-Durchschnitt für den jeweiligen Wochentag berechnet. Dies entspricht
                den gesetzlichen Vorgaben (EFZG bzw. §11 BUrlG). Die Stunden sind in diesem Fall
                gesperrt und können nicht manuell geändert werden.
              </p>

              <h4 className="text-base font-medium text-gray-800 dark:text-gray-100">Urlaubstage-Anzeige</h4>
              <p>
                In der Kalender-Legende sehen Sie <strong>X von Y Tagen genutzt</strong> — das zeigt,
                wie viele Ihrer Urlaubstage Sie bereits verbraucht haben.
              </p>

              <h4 className="text-base font-medium text-gray-800 dark:text-gray-100">Gestrichelte Umrandung</h4>
              <p>
                Urlaubstage mit <strong>gestrichelter Umrandung</strong> haben vorläufig berechnete
                Stunden. Diese Stunden basieren auf dem aktuellen 13-Wochen-Durchschnitt und werden
                automatisch aktualisiert, sobald neue Arbeitsdaten vorliegen. Mehr dazu unter{' '}
                <strong>Vorläufige Urlaubsstunden</strong>.
              </p>
            </div>
          </section>

          {/* Urlaub & Feiertage */}
          <section id="help-urlaub-feiertage" className="scroll-mt-24">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 border-b border-gray-200 dark:border-gray-700 pb-2">
              Urlaub &amp; Feiertage
            </h3>
            <div className="prose dark:prose-invert max-w-none space-y-4 text-sm text-gray-700 dark:text-gray-300">
              <h4 className="text-base font-medium text-gray-800 dark:text-gray-100">Urlaubsanspruch</h4>
              <p>
                Ihr individueller Urlaubsanspruch wird automatisch berechnet — basierend auf Ihren
                tatsächlichen Arbeitstagen pro Woche im Verhältnis zur Vollzeitstelle:
              </p>
              <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 font-mono text-xs">
                Urlaubsanspruch = (Betriebsurlaub ÷ Betriebstage) × Ø Arbeitstage/Woche
              </div>
              <p>
                Die Rundung erfolgt nach <strong>§5 BUrlG</strong>: Ab 0,5 wird aufgerundet,
                darunter abgerundet.
              </p>

              <h4 className="text-base font-medium text-gray-800 dark:text-gray-100">Bundesland &amp; Feiertage</h4>
              <p>
                In den Einstellungen ist ein Bundesland hinterlegt. Die App berücksichtigt automatisch
                alle bundesweiten und landesspezifischen Feiertage (z.B. Fronleichnam in Bayern,
                Reformationstag in Sachsen). Bewegliche Feiertage wie Ostern und Pfingsten werden
                korrekt berechnet.
              </p>

              <h4 className="text-base font-medium text-gray-800 dark:text-gray-100">Betriebsferien</h4>
              <p>
                HR kann den Betriebsferien-Zeitraum unter <strong>Einstellungen</strong> konfigurieren.
                Für Festangestellte (Festgehalt) werden Betriebsferien als Urlaubstage abgezogen.
                Für Stundenlohn-Mitarbeiter sind Betriebsferien arbeitsfreie Tage ohne Urlaubsabzug —
                Arbeitsstunden können nicht eingetragen werden, aber Urlaub oder Krankheit ist manuell möglich.
                Fällt ein Betriebsferien-Tag auf einen Feiertag, wird kein Urlaub abgezogen.
              </p>

              <h4 className="text-base font-medium text-gray-800 dark:text-gray-100">Urlaubsübertrag</h4>
              <p>
                Nicht genommener Urlaub wird ins Folgejahr übertragen und verfällt nach deutschem
                Arbeitsrecht am <strong>31. März</strong>. Die App zeigt eine Warnung an, wenn
                Restürlaub aus dem Vorjahr bald verfällt.
              </p>

              <h4 className="text-base font-medium text-gray-800 dark:text-gray-100">Buchungslimit</h4>
              <p>
                Sie können maximal so viele Urlaubstage buchen, wie Ihnen für das Jahr zustehen.
                Das Limit setzt sich zusammen aus: <strong>Jahresanspruch + Übertrag aus dem
                Vorjahr + eventuelle Anpassungen</strong> (Sonderurlaub, Bildungsurlaub, Korrekturen).
                Wenn Sie Ihr Limit erreicht haben, ist die Urlaubs-Checkbox im Bearbeitungsdialog
                nicht mehr verfügbar. Wenden Sie sich an HR, wenn Sie weitere Urlaubstage benötigen.
              </p>

              <h4 className="text-base font-medium text-gray-800 dark:text-gray-100">Erdient bis heute</h4>
              <p>
                Ihr Urlaubsanspruch entsteht nicht am Jahresanfang auf einen Schlag, sondern wächst
                anteilig mit jedem vollen Arbeitsmonat. <strong>Erdient bis heute</strong> zeigt,
                wie viel Ihnen bereits zusteht. Wenn Sie mehr Urlaub nehmen als bisher erdient,
                erscheint ein <strong>oranges Segment</strong> im Fortschrittsbalken mit einem
                entsprechenden Hinweis.
              </p>

              <h4 className="text-base font-medium text-gray-800 dark:text-gray-100">Urlaubsentgelt</h4>
              <p>
                Das Urlaubsentgelt wird nach dem <strong>13-Wochen-Durchschnitt</strong> berechnet
                (§11 BUrlG). Dabei werden die 91 Tage vor dem ersten Tag des letzten Urlaubsblocks
                als Referenzzeitraum herangezogen. Urlaubs-, Krankheits- und Feiertage werden aus
                der Berechnung ausgeschlossen.
              </p>
            </div>
          </section>

          {/* Urlaubsbuchung */}
          <section id="help-urlaubsbuchung" className="scroll-mt-24">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 border-b border-gray-200 dark:border-gray-700 pb-2">
              Urlaubsbuchung
            </h3>
            <div className="prose dark:prose-invert max-w-none space-y-4 text-sm text-gray-700 dark:text-gray-300">
              <h4 className="text-base font-medium text-gray-800 dark:text-gray-100">Einzelne Urlaubstage buchen</h4>
              <p>
                Klicken Sie im Kalender auf den gewünschten Tag und aktivieren Sie die{' '}
                <strong>Urlaubs-Checkbox</strong>. Die Urlaubsstunden werden automatisch auf Basis
                Ihres 13-Wochen-Durchschnitts berechnet. Für halbe Urlaubstage gibt es eine
                separate Checkbox — die Stunden werden dann halbiert.
              </p>

              <h4 className="text-base font-medium text-gray-800 dark:text-gray-100">Urlaubsblock buchen</h4>
              <p>
                Für mehrere zusammenhängende Urlaubstage nutzen Sie den{' '}
                <strong>Urlaubsblock-Dialog</strong>. Wählen Sie Start- und Enddatum — Wochenenden,
                Feiertage und Krankheitstage werden automatisch übersprungen.
              </p>

              <h4 className="text-base font-medium text-gray-800 dark:text-gray-100">Buchungslimit</h4>
              <p>
                Sie können pro Jahr maximal so viele Urlaubstage buchen, wie Ihnen zustehen:
              </p>
              <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 font-mono text-xs">
                Limit = Jahresanspruch + Übertrag + Anpassungen (Sonderurlaub, Bildungsurlaub, Korrekturen)
              </div>
              <p>
                Wenn Sie Ihr Limit erreicht haben, ist die <strong>Urlaubs-Checkbox deaktiviert</strong>.
                Falls Sie weitere Urlaubstage benötigen, wenden Sie sich an die Personalabteilung (HR).
                HR kann Sonderurlaub, Bildungsurlaub oder Korrekturen vornehmen.
              </p>

              <h4 className="text-base font-medium text-gray-800 dark:text-gray-100">Über dem erdienten Anspruch</h4>
              <p>
                Ihr Urlaubsanspruch wächst monatlich an. Wenn Sie mehr Urlaub nehmen als bisher
                erdient, erscheint ein <strong>oranges Segment</strong> im Fortschrittsbalken auf
                der Startseite und ein Hinweis, dass der restliche Anspruch mit jedem weiteren
                Arbeitsmonat entsteht. Sie können trotzdem buchen, solange Ihr Jahresanspruch
                nicht überschritten ist.
              </p>

              <h4 className="text-base font-medium text-gray-800 dark:text-gray-100">Abwesenheit vormerken</h4>
              <p>
                Wenn Sie eine Abwesenheit planen möchten, ohne sofort Urlaubstage zu verbrauchen,
                nutzen Sie den <strong>Vormerken-Modus</strong> im Urlaubsblock-Dialog. Wechseln
                Sie dort auf den Tab <strong>&bdquo;Vormerken&ldquo;</strong>, wählen Sie Zeitraum und
                Abwesenheitsart und bestätigen Sie.
              </p>
              <p>
                Vorgemerkte Tage erscheinen im Kalender mit einer{' '}
                <strong>violetten diagonalen Schraffur</strong>. Es wird kein Urlaubstag abgezogen
                und kein Kalender-Eintrag angelegt — erst nach dem Einreichen und ggf. der
                Genehmigung.
              </p>

              <h4 className="text-base font-medium text-gray-800 dark:text-gray-100">Meine Abwesenheitsanträge</h4>
              <p>
                Auf Ihrer Startseite finden Sie die Karte <strong>&bdquo;Meine Abwesenheitsanträge&ldquo;</strong>.
                Dort sehen Sie aktuelle Anträge mit Status-Badge:
              </p>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Geplant</strong> (violett) — Vorgemerkt, noch nicht eingereicht. Aktionen: Einreichen oder Stornieren.</li>
                <li><strong>Beantragt</strong> (blau) — Eingereicht, wartet auf Genehmigung.</li>
                <li><strong>Genehmigt</strong> (grün) — Genehmigt und im Kalender eingetragen.</li>
                <li><strong>Abgelehnt</strong> (rot) — Abgelehnt, kein Kalender-Eintrag.</li>
                <li><strong>Storniert</strong> (grau) — Manuell storniert.</li>
              </ul>
              <p>
                Die Karte zeigt die drei neuesten relevanten Anträge — weitere laden Sie per Klick
                auf &bdquo;Alle anzeigen&ldquo;.
              </p>
            </div>
          </section>

          {/* Vorläufige Urlaubsstunden */}
          <section id="help-vorlaeufig" className="scroll-mt-24">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 border-b border-gray-200 dark:border-gray-700 pb-2">
              Vorläufige Urlaubsstunden
            </h3>
            <div className="prose dark:prose-invert max-w-none space-y-4 text-sm text-gray-700 dark:text-gray-300">
              <h4 className="text-base font-medium text-gray-800 dark:text-gray-100">Was bedeutet &bdquo;vorläufig&ldquo;?</h4>
              <p>
                Wenn Sie Urlaub für die <strong>Zukunft</strong> eintragen, werden die Urlaubsstunden
                auf Basis Ihres <strong>aktuellen 13-Wochen-Durchschnitts</strong> berechnet. Da Sie
                bis zum Urlaubsantritt weiterarbeiten, kann sich dieser Durchschnitt noch ändern.
                Solche Urlaubstage sind im Kalender mit einer <strong>gestrichelten Umrandung</strong>{' '}
                gekennzeichnet und im Bearbeitungsdialog als &bdquo;vorläufig&ldquo; markiert.
              </p>

              <h4 className="text-base font-medium text-gray-800 dark:text-gray-100">Warum ändern sich die Stunden?</h4>
              <p>
                Der 13-Wochen-Durchschnitt berücksichtigt Ihre tatsächlichen Arbeitsstunden der
                letzten 91 Tage. Wenn Sie in den Wochen vor dem Urlaub mehr oder weniger arbeiten,
                ändert sich der Durchschnitt und damit auch die berechneten Urlaubsstunden. Die
                App aktualisiert diese Werte automatisch bei jeder Änderung Ihrer Einträge.
              </p>

              <h4 className="text-base font-medium text-gray-800 dark:text-gray-100">Wann werden die Stunden endgültig?</h4>
              <p>
                Sobald der Urlaubstag in der Vergangenheit liegt, steht der 13-Wochen-Durchschnitt
                fest und die Stunden sind endgültig. Beim <strong>Lohn-Export</strong> berechnet das
                System den endgültigen Wert automatisch nach, bevor die Daten exportiert werden.
              </p>
            </div>
          </section>

          {/* Einstellungen */}
          <section id="help-einstellungen" className="scroll-mt-24">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 border-b border-gray-200 dark:border-gray-700 pb-2">
              Einstellungen
            </h3>
            <div className="prose dark:prose-invert max-w-none space-y-4 text-sm text-gray-700 dark:text-gray-300">
              <h4 className="text-base font-medium text-gray-800 dark:text-gray-100">Betriebsdaten</h4>
              <p>
                Die Betriebsdaten umfassen die Rahmenbedingungen Ihres Beschäftigungsverhältnisses
                und können nur von der <strong>HR-Abteilung</strong> geändert werden:
              </p>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Bundesland</strong> — Bestimmt die geltenden Feiertage</li>
                <li><strong>Jahresurlaub Vollzeit</strong> — Urlaubstage bei Vollzeit (Standard: 30)</li>
                <li><strong>Arbeitstage/Woche</strong> — 5- oder 6-Tage-Woche</li>
                <li><strong>Sollstunden/Tag</strong> — Tägliche Arbeitszeit bei Vollzeit (Standard: 8h)</li>
                <li><strong>Stundenlohn (brutto)</strong> — Für die Urlaubsentgelt-Berechnung</li>
              </ul>
              <p>
                Die für Sie geltenden Werte sehen Sie auf Ihrer Startseite in der{' '}
                <strong>Berechnungserklärung</strong> neben dem Urlaubssaldo.
              </p>
              <ScreenshotPlaceholder description="Screenshot: Betriebsdaten-Einstellungen (HR-Ansicht)" />

              <h4 className="text-base font-medium text-gray-800 dark:text-gray-100">Neuberechnung</h4>
              <p>
                Alle Berechnungen (Urlaubsanspruch, Urlaubsentgelt, Monatsübersicht) werden
                automatisch aktualisiert, sobald Sie Einträge oder Einstellungen ändern. Es ist
                kein manuelles Neuberechnen nötig.
              </p>

              <h4 className="text-base font-medium text-gray-800 dark:text-gray-100">Dark Mode</h4>
              <p>
                Über das Sonnen-/Mond-Symbol in der Kopfzeile können Sie zwischen hellem und dunklem
                Design wechseln. Die Einstellung wird gespeichert.
              </p>
            </div>
          </section>

          {/* Auswertung */}
          <section id="help-auswertung" className="scroll-mt-24">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 border-b border-gray-200 dark:border-gray-700 pb-2">
              Auswertung &amp; Dashboard
            </h3>
            <div className="prose dark:prose-invert max-w-none space-y-4 text-sm text-gray-700 dark:text-gray-300">
              <h4 className="text-base font-medium text-gray-800 dark:text-gray-100">Startseite</h4>
              <p>
                Auf Ihrer <strong>Startseite</strong> sehen Sie auf einen Blick die wichtigsten
                Kennzahlen des gewählten Jahres: Gesamtstunden, Arbeitstage, Urlaubstage und
                Krankheitstage. Darunter befindet sich der Kalender und aufklappbare Detail-Bereiche.
              </p>

              <h4 className="text-base font-medium text-gray-800 dark:text-gray-100">Urlaubssaldo &amp; Berechnungserklärung</h4>
              <p>
                Der <strong>Urlaubssaldo</strong> auf der Startseite zeigt zwei Fortschrittsbalken:
              </p>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Genommen</strong> — Wie viele Urlaubstage Sie bereits verbraucht haben (blau = im erdienten Rahmen, orange = über dem erdienten Anspruch)</li>
                <li><strong>Erdient bis heute</strong> — Wie viel Ihres Jahresanspruchs Ihnen zum aktuellen Zeitpunkt zusteht (grün)</li>
              </ul>
              <p>
                Daneben zeigt die <strong>Berechnungserklärung</strong> Ihre persönliche Formel:
                Betriebsurlaub ÷ Betriebstage × Ihre Ø Arbeitstage/Woche = Ihr Anspruch.
                Bei einem nicht vollen Beschäftigungsjahr wird zusätzlich die{' '}
                <strong>Teiljahr-Berechnung</strong> nach §5 BUrlG angezeigt.
              </p>

              <h4 className="text-base font-medium text-gray-800 dark:text-gray-100">Monatsübersicht</h4>
              <p>
                Unter <strong>Monatsübersicht</strong> (aufklappbar) sehen Sie pro Monat eine
                Zusammenfassung: Arbeitsstunden, Arbeitstage, Feiertage und Urlaubstage. Am Ende
                stehen die Jahressummen.
              </p>

              <h4 className="text-base font-medium text-gray-800 dark:text-gray-100">Details</h4>
              <p>
                Unter <strong>Details anzeigen</strong> (aufklappbar) finden Sie vier Bereiche:
              </p>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Arbeitszeitstatistik</strong> — Gesamtstunden, Arbeitstage, Ø Stunden/Tag, Ø Tage/Woche</li>
                <li><strong>Urlaubsanspruch §5 BUrlG</strong> — Detaillierte Berechnung mit Formel</li>
                <li><strong>Urlaubsentgelt §11 BUrlG</strong> — 13-Wochen-Durchschnitt, Urlaubsstunden, Entgelt</li>
                <li><strong>Sonstiges</strong> — Feiertage, Krankheitstage</li>
              </ul>

              <h4 className="text-base font-medium text-gray-800 dark:text-gray-100">Urlaubsübertrag</h4>
              <p>
                Falls Sie Resturlaub aus dem Vorjahr haben, wird dieser automatisch auf der
                Startseite angezeigt. Beachten Sie, dass der Übertrag nach deutschem Arbeitsrecht
                am 31. März verfällt — die App warnt Sie rechtzeitig.
              </p>

              <p className="text-xs text-gray-500 dark:text-gray-400">
                <strong>Hinweis:</strong> Export-Funktionen (CSV, PDF) stehen ausschließlich im
                HR-Dashboard zur Verfügung.
              </p>
            </div>
          </section>

          {/* Manager-Bereich */}
          {isManager && (
            <section id="help-manager" className="scroll-mt-24">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 border-b border-gray-200 dark:border-gray-700 pb-2">
                Manager-Bereich
              </h3>
              <div className="prose dark:prose-invert max-w-none space-y-4 text-sm text-gray-700 dark:text-gray-300">
                <h4 className="text-base font-medium text-gray-800 dark:text-gray-100">Team-Dashboard</h4>
                <p>
                  Im Tab <strong>Mein Team</strong> sehen Sie alle Mitglieder Ihrer Abteilung als
                  Kacheln — mit Name, E-Mail, Abteilungs-Badge und Rollen-Badge. Die Ansicht nutzt
                  die volle Breite und passt sich responsiv an (2–5 Spalten je nach Bildschirmgröße).
                </p>
                <p>
                  Über das <strong>Suchfeld</strong> können Sie Teammitglieder nach Name oder E-Mail
                  filtern. Der <strong>Ansicht-Umschalter</strong> (Kachel/Liste) bietet eine
                  kompakte Listenansicht als Alternative.
                </p>
                <ScreenshotPlaceholder description="Screenshot: Team-Dashboard mit Kacheln der Teammitglieder" />

                <h4 className="text-base font-medium text-gray-800 dark:text-gray-100">Team-Kalender</h4>
                <p>
                  Klicken Sie auf eine <strong>Kachel</strong> oder das <strong>Kalender-Icon</strong>,
                  um den Kalender des Teammitglieds zu öffnen. Dort sehen Sie alle Einträge im Detail
                  und können zwischen Jahren wechseln.
                </p>

                <h4 className="text-base font-medium text-gray-800 dark:text-gray-100">Manager-Anmerkungen</h4>
                <p>
                  Im Kalender eines Teammitglieds können Sie zu einzelnen Tagen
                  <strong> Anmerkungen</strong> hinterlassen. Diese sind für den Mitarbeiter
                  sichtbar und werden mit Ihrem Namen und Zeitstempel versehen. Klicken Sie dazu
                  auf den gewünschten Tag im Kalender.
                </p>

                <h4 className="text-base font-medium text-gray-800 dark:text-gray-100">Berechtigungen</h4>
                <p>Als Manager können Sie:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Kalender Ihrer Teammitglieder einsehen — mit eingeschränkter Sichtbarkeit auf Gesundheitsdaten (siehe nächster Abschnitt)</li>
                  <li>Manager-Anmerkungen zu einzelnen Tagen hinzufügen</li>
                  <li>Abwesenheits-Anträge auf administrative Typen (Urlaub, Sonderurlaub, Bildungsurlaub etc.) genehmigen oder ablehnen</li>
                </ul>
                <p>Als Manager können Sie <strong>nicht</strong>:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Mitarbeiter löschen oder Rollen/Abteilungen ändern</li>
                  <li>Einträge direkt bearbeiten</li>
                  <li>Den Typ einer Krankheit, Reha, Mutterschutz, Pflegezeit etc. einsehen (DSGVO Art. 9)</li>
                  <li>Anträge auf solche Typen genehmigen — diese laufen direkt über HR</li>
                </ul>

                <h4 className="text-base font-medium text-gray-800 dark:text-gray-100">Sichtbarkeit von Abwesenheiten (DSGVO Art. 9 / § 26 BDSG)</h4>
                <p>
                  Gesundheitsdaten sind nach DSGVO besondere personenbezogene Daten. Sie sehen
                  daher im Team-Kalender nicht alle Abwesenheiten gleich detailliert:
                </p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>
                    <strong>Voll sichtbar</strong> (Typ + Details, eigenes Icon und Farbe):
                    Urlaub, Halber Urlaub, Sonderurlaub, Bildungsurlaub, Geplante Abwesenheit,
                    Überstundenabbau, Unbezahlter Urlaub, Elternzeit, Betriebsferien.
                  </li>
                  <li>
                    <strong>Nur &bdquo;abwesend&ldquo;</strong> (neutraler grauer Block ohne Typ-Anzeige):
                    Krankheit, Kind krank, Reha/Kur, Mutterschutz, Pflegezeit, Quarantäne.
                    Sie sehen <em>dass</em> der Mitarbeiter abwesend ist — was Sie für die
                    Personalplanung brauchen — aber nicht <em>warum</em>.
                  </li>
                  <li>
                    <strong>Komplett unsichtbar</strong> (kein Eintrag im Kalender):
                    BEM-Maßnahmen und vergleichbare HR-interne Vorgänge.
                  </li>
                </ul>
                <p>
                  Die Filterung passiert auf dem Server, nicht im Browser. Auch über die
                  Entwicklerwerkzeuge können verborgene Typen nicht ausgelesen werden.
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  <strong>Hinweis:</strong> Die Zuordnung kann HR pro Abwesenheitsart anpassen
                  (Einstellungen → Abwesenheitsarten). Die hier gelisteten Defaults gelten,
                  solange HR nichts ändert.
                </p>

                <h4 className="text-base font-medium text-gray-800 dark:text-gray-100">Abwesenheitsanträge genehmigen</h4>
                <p>
                  Unter dem Tab <strong>&bdquo;Genehmigungen&ldquo;</strong> sehen Sie alle offenen
                  Antragsanfragen Ihrer Teammitglieder auf administrative Typen (Urlaub,
                  Sonderurlaub, Bildungsurlaub, Elternzeit etc.). Pro Antrag können Sie:
                </p>
                <ul className="list-disc pl-5 space-y-1">
                  <li><strong>Genehmigen</strong> — Der Antrag wird genehmigt und der Kalender-Eintrag angelegt. Optional können Sie eine Anmerkung hinterlassen.</li>
                  <li><strong>Ablehnen</strong> — Der Antrag wird abgelehnt. Eine Begründung ist erforderlich.</li>
                </ul>
                <p>
                  <strong>Wichtig:</strong> Anträge auf gesundheits- oder HR-sensible
                  Abwesenheitstypen (Mutterschutz, Pflegezeit, BEM-Maßnahmen etc.) erscheinen
                  hier <em>nicht</em>. Sie bekommen dafür auch keine Email-Benachrichtigung —
                  diese Anträge gehen direkt an HR. Auch das Badge mit der Anzahl offener
                  Anträge zählt nur die für Sie tatsächlich genehmigungsfähigen Anträge.
                </p>
              </div>
            </section>
          )}

          {/* HR-Bereich */}
          {isHR && (
            <section id="help-hr" className="scroll-mt-24">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 border-b border-gray-200 dark:border-gray-700 pb-2">
                HR-Bereich
              </h3>
              <div className="prose dark:prose-invert max-w-none space-y-4 text-sm text-gray-700 dark:text-gray-300">
                <h4 className="text-base font-medium text-gray-800 dark:text-gray-100">Mitarbeiterliste</h4>
                <p>
                  Im <strong>HR-Dashboard</strong> sehen Sie alle Mitarbeiter des Unternehmens als
                  Kacheln mit Name, E-Mail, Abteilungs-Badge, Rollen-Badge und letzter Aktivität.
                  Über die Suche können Sie filtern, der Ansicht-Umschalter bietet Kachel- und Listenansicht.
                </p>
                <ScreenshotPlaceholder description="Screenshot: HR-Dashboard mit Mitarbeiterkacheln" />

                <h4 className="text-base font-medium text-gray-800 dark:text-gray-100">Mitarbeiterdetails</h4>
                <p>
                  Klicken Sie auf einen Mitarbeiter, um dessen vollständigen Kalender, Auswertungen
                  und Einstellungen einzusehen. Als HR haben Sie vollen Lesezugriff auf alle Daten
                  und können Kalendereinträge direkt bearbeiten.
                </p>

                <h4 className="text-base font-medium text-gray-800 dark:text-gray-100">Lohn-Export</h4>
                <p>
                  Das HR-Dashboard enthält einen <strong>Lohn-Export</strong>-Bereich für die
                  Lohnbuchhaltung. Hier können Sie Daten im ISWL-Format exportieren.
                </p>
                <p>
                  <strong>Abrechnungszeitraum:</strong> Die Abrechnungsmonate laufen nicht vom 1.
                  bis zum 30./31., sondern vom <strong>21. des Vormonats bis zum 20. des aktuellen
                  Monats</strong>. So umfasst z.B. der Abrechnungsmonat Februar den Zeitraum
                  21. Januar – 20. Februar.
                </p>
                <p><strong>Sammel-Export</strong> (bei Auswahl &quot;Alle Mitarbeiter&quot;):</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Exportiert alle aktiven Mitarbeiter in <strong>einer CSV-Datei</strong></li>
                  <li>Pro Mitarbeiter eine Zeile mit Lohnarten (LA 1000 Arbeit, LA 1600 Urlaub, LA 1650 Krank, LA 1012 Feiertag, LA 8560 Urlaubstage)</li>
                  <li>Die Anzeige &quot;X von Y Mitarbeiter werden exportiert&quot; zeigt, wie viele Mitarbeiter nach Filterung berücksichtigt werden</li>
                </ul>
                <p><strong>Einzel-Export</strong> (bei Auswahl eines Mitarbeiters):</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li><strong>Lohn-CSV (Monat)</strong> — Einzelner Abrechnungsmonat nach Lohnarten</li>
                  <li><strong>Lohn-CSV (Jahr)</strong> — Alle 12 Monate + Jahressummen</li>
                  <li><strong>Tagesdetails</strong> — Tag-für-Tag-Aufstellung mit Abrechnungszeitraum-Zwischensummen</li>
                  <li><strong>PDF-Bericht</strong> — Jahresübersicht als druckbares PDF</li>
                </ul>
                <ScreenshotPlaceholder description="Screenshot: Lohn-Export-Panel mit Mitarbeiter- und Monatsauswahl" />

                <h4 className="text-base font-medium text-gray-800 dark:text-gray-100">Beschäftigungszeitraum</h4>
                <p>
                  In den Mitarbeiterdetails können Sie den <strong>Beschäftigungszeitraum</strong> pflegen:
                </p>
                <ul className="list-disc pl-5 space-y-1">
                  <li><strong>Beschäftigt seit</strong> — Eintrittsdatum des Mitarbeiters</li>
                  <li><strong>Beschäftigt bis</strong> — Austrittsdatum (leer = noch aktiv)</li>
                </ul>
                <p>
                  Mitarbeiter mit einem <strong>Austrittsdatum vor dem Abrechnungszeitraum</strong> werden
                  im Sammel-Export automatisch ausgeschlossen. Ein roter <strong>&quot;Ausgeschieden&quot;</strong>-Badge
                  wird an der Kachel angezeigt.
                </p>

                <h4 className="text-base font-medium text-gray-800 dark:text-gray-100">Export-Ausschluss</h4>
                <p>
                  Mit der Checkbox <strong>&quot;Vom Lohn-Export ausschließen&quot;</strong> können Sie
                  einzelne Mitarbeiter dauerhaft vom Sammel-Export ausnehmen — z.B. Geschäftsführer
                  oder Werkstudenten mit eigener Abrechnung.
                </p>
                <p>
                  Ausgeschlossene Mitarbeiter werden mit einem grauen Badge in der Mitarbeiterliste
                  gekennzeichnet. Einzel-Exporte sind davon nicht betroffen.
                </p>

                <h4 className="text-base font-medium text-gray-800 dark:text-gray-100">Rollenverwaltung</h4>
                <p>
                  Rollen werden über die Microsoft-Azure-AD-Gruppenzugehörigkeit gesteuert. Um die
                  Rolle eines Mitarbeiters zu ändern, passen Sie die Gruppenmitgliedschaft im Azure-Portal an.
                </p>

                <h4 className="text-base font-medium text-gray-800 dark:text-gray-100">Organigramm</h4>
                <p>
                  Über <strong>Organigramm</strong> im HR-Bereich sehen Sie die Unternehmensstruktur
                  als vertikalen Baum. Per <strong>Drag &amp; Drop</strong> können Manager-Zuordnungen
                  direkt im Diagramm verschoben werden. Die App erkennt Zyklen (Manager ist gleichzeitig
                  Untergebener) und verhindert ungültige Zuordnungen automatisch.
                </p>

                <h4 className="text-base font-medium text-gray-800 dark:text-gray-100">Beschäftigungshistorie</h4>
                <p>
                  In den Mitarbeiterdetails zeigt die <strong>Beschäftigungshistorie</strong> alle
                  Vertragsänderungen als Timeline. Jeder Eintrag enthält einen Diff der geänderten
                  Felder (z.B. Beschäftigungsart, Lohnart, Abteilung). Bei Vertragswechseln innerhalb
                  des Jahres berechnet die App den Urlaubsanspruch anteilig nach §5 BUrlG — und der
                  Lohn-Export spaltet automatisch nach Zeiträumen auf.
                </p>

                <h4 className="text-base font-medium text-gray-800 dark:text-gray-100">
                  Abwesenheitsarten verwalten (Sichtbarkeit für Abteilungsleitung)
                </h4>
                <p>
                  Unter <strong>Einstellungen → Abwesenheitsarten</strong> verwalten Sie die im
                  System verfügbaren Abwesenheitstypen. Pro Typ konfigurieren Sie Name, Kurzname,
                  Farbe, Icon, ob er als Urlaub oder Krankheit zählt, ob er einen Antrag erfordert
                  und — für DSGVO-konformen Umgang mit Gesundheitsdaten — die
                  <strong> Sichtbarkeit für die Abteilungsleitung</strong>:
                </p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>
                    <strong>Voll sichtbar (Typ + Details)</strong> — die Abteilungsleitung sieht
                    den Typ mit Farbe und Icon. Standard für administrative Typen wie Urlaub,
                    Sonderurlaub, Bildungsurlaub, Elternzeit, Geplante Abwesenheit, Überstundenabbau.
                  </li>
                  <li>
                    <strong>Nur &bdquo;abwesend&ldquo; (Typ verborgen)</strong> — die Abteilungsleitung sieht
                    nur einen neutralen grauen Block; der Typ bleibt verborgen. Standard für
                    Gesundheitsdaten nach DSGVO Art. 9: Krankheit, Kind krank (§ 45 SGB V),
                    Reha/Kur, Mutterschutz, Pflegezeit, Quarantäne.
                  </li>
                  <li>
                    <strong>Nur für HR sichtbar</strong> — die Abteilungsleitung sieht den Eintrag
                    gar nicht. Standard für BEM-Maßnahmen und vergleichbar sensible HR-interne
                    Vorgänge.
                  </li>
                </ul>
                <p>
                  Diese Wahl steuert nicht nur die Kalender-Anzeige, sondern auch das
                  <strong> Genehmigungs-Routing</strong>: Anträge auf Typen, die der Manager
                  nicht sehen darf, gehen direkt an HR — der Manager bekommt weder eine
                  Email noch einen Listing-Eintrag. Damit ist sichergestellt, dass HR
                  zuständig bleibt für gesundheits- oder personalpolitisch sensible Themen.
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  <strong>Rechtlicher Hintergrund:</strong> DSGVO Art. 9 (besondere
                  Kategorien personenbezogener Daten — u.a. Gesundheitsdaten) und § 26 BDSG
                  (Datenminimierung im Beschäftigungskontext) verlangen, dass die
                  Abteilungsleitung nur das sieht, was sie für ihre Aufgabe (Personalplanung,
                  Vertretungsregelung, Genehmigung) tatsächlich braucht. <em>Dass</em> jemand
                  abwesend ist, gehört dazu — <em>warum</em> in den allermeisten Fällen nicht.
                </p>
                <ScreenshotPlaceholder description='Screenshot: Abwesenheitsart-Dialog mit Select-Feld „Sichtbarkeit für Abteilungsleitung"' />

              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
